<template>
  <div>
    <div
      class="d-flex flex-row w-100 align-items-center justify-content-between"
      @click="expandTraceabilityDetails(orderIndex)"
    >
      <data-cell :size="25">
        <h3 class="text-color-brand-primary">
          <span :class="{ 'highlight-search': searchParam === PARAM_SEARCH_OS_ALVARO }">
            {{ body.orderId || EMPTY_FIELD_OS_ALVARO }}
          </span>
        </h3>

        <p class="text-color-tundora overflow-text">
          <span>
            {{ body?.idOsLis }}
          </span>
        </p>
      </data-cell>
      <data-cell :size="35">
        <h3 class="text-color-jaffa text-capitalize">
          <span
            :class="{ 'highlight-search': searchParam === PARAM_SEARCH_PATIENT_NAME }"
          >
            {{ body.patientName }}
          </span>
        </h3>
      </data-cell>
      <data-cell :size="25">
        <h3 class="text-color-brand-primary">
          {{ samplesText }}
        </h3>
        <p class="text-color-tundora overflow-text">
          {{ formattedDestinations }}

          <md-tooltip>
            {{ formattedDestinations }}
          </md-tooltip>
        </p>
      </data-cell>

      <data-cell
        class="flex flex-row align-center justify-end gap-2"
        :size="15"
      >
        <div
          v-if="canUpdateCollectionDate(body)"
          class="status-icon icon-edit-alt m-0"
          @click.stop="openUpdateCollectionDateModal"
        >
          <md-tooltip
            md-delay="400"
            md-direction="top"
          >
            Atualizar Data e Hora da Coleta
          </md-tooltip>
        </div>

        <div
          v-if="canIncludeExam(body)"
          class="status-icon icon-insert-trans m-0"
          @click.capture.stop="openInclusionExamModal(body)"
        >
          <md-tooltip
            md-delay="400"
            md-direction="top"
          >
            Incluir Exame
          </md-tooltip>
        </div>

        <div
          v-if="hasLaudoLiberado"
          style="max-height: 30px"
        >
          <dropdown-actions
            v-if="!isDownloading"
            :items="pdfActionsItems"
            trigger-icon-class="icon-download-alt"
            tooltip-text="Ações dos resultados"
            tooltip-direction="left"
            trigger-icon-size="30"
          />
          <spinner
            v-else-if="isDownloading"
            class="status-icon"
          />
        </div>

        <div>
          <data-show-off
            :is-open="body.expanded"
            :is-loading="body.isLoading"
          />
        </div>
      </data-cell>
    </div>
    <transition name="expand">
      <data-table
        v-if="body.expanded"
        :header="table.subHeader"
        type="subtable"
        @info-icon-rastreabilidade="openStatusGuideModal()"
      >
        <div
          v-for="(sample, index) in body.samples"
          :key="'sample-' + sample.codeSample + index"
          class="separator"
        >
          <div
            v-for="(examSample, i) in sample.exams"
            :key="`examSample-${i}-${examSample.id || examSample.codeExamBrand}`"
            class="data-table__body__row--subtable new-subtable exam-row"
          >
            <data-cell
              :size="15"
              class="m-0"
            >
              <div
                v-if="i === 0"
                class="new-subtable__exam-details"
              >
                <p>
                  {{ sample.codeSample }}
                </p>
                <div
                  class="batch"
                  :style="{ cursor: sample.lotId ? 'pointer' : 'default' }"
                >
                  <span
                    v-if="sample.lotId"
                    style="margin-top: 0px"
                    @click="goToLotDay(sample)"
                  >
                    {{ sample.lotId }}
                  </span>
                  <span
                    v-else
                    style="margin-top: 0px"
                  >
                    NÃO LOTEADO
                  </span>
                </div>
              </div>
            </data-cell>
            <data-cell
              class="traceability_sample_exam"
              :size="10"
            >
              <div>
                <p>
                  {{ examSample.codeExamBrand }}
                </p>
                <md-tooltip
                  md-delay="400"
                  md-direction="top"
                >
                  {{ examSample.codeExamBrand }}
                </md-tooltip>
              </div>
            </data-cell>
            <data-cell
              class="traceability_sample_exam"
              :size="35"
            >
              <TraceabilityProgressBar
                :statuses="examSample"
                :order-id="body.orderId"
                :sample="sample"
                @openMap="openMap"
              />
            </data-cell>
            <data-cell
              class="traceability_sample_exam"
              :size="15"
            >
              <p class="text-color-tundora details">
                <strong>
                  {{ convertDataWithoutTimeZoneForDate(examSample.currentStatusDate) }}
                </strong>
              </p>
            </data-cell>
            <data-cell
              class="traceability_sample_exam"
              :size="15"
            >
              <div class="new-subtable__promised-date traceability_sample_exam__date">
                <template v-if="showDueDate(examSample)">
                  <p class="text-color-tundora details">
                    <strong>
                      {{ convertDataWithoutTimeZoneForDate(examSample.dueDate) }}
                    </strong>
                  </p>
                  <md-tooltip>
                    {{ convertDataWithoutTimeZoneForDate(examSample.dueDate) }}
                  </md-tooltip>
                </template>
                <p
                  v-else
                  class="text-color-tundora details"
                >
                  {{ emptyFieldDueDate }}
                </p>
              </div>
            </data-cell>
            <data-cell
              class="traceability_sample_exam flex justify-end"
              :size="10"
            >
              <template v-if="canDeleteExam(examSample)">
                <div
                  class="status-icon icon-delete-alt m-0"
                  @click.stop="openDeleteExamModal(examSample)"
                >
                  <md-tooltip
                    md-delay="400"
                    md-direction="top"
                  >
                    Excluir
                  </md-tooltip>
                </div>
              </template>
            </data-cell>
          </div>
        </div>
      </data-table>
    </transition>

    <TraceabilityShowReadyExamsModal :html-for-download="htmlForDownload" />
    <TraceabilityCollectionDateModal
      ref="updateCollectionDateModal"
      :initial-date="selectedSampleDate"
      :initial-time="selectedSampleTime"
      :order-id="body.orderId"
      @update="handleUpdateCollectionDate"
    />

    <modal
      ref="traceabilitytraceabilityInsertExamAfterTransferred"
      title="Incluir novo exame"
      template="question"
      ref-name="traceabilitytraceabilityInsertExamAfterTransferred"
      first-button-text="Cancelar"
      second-button-text="Confirmar"
      :close-button-enabled="true"
      :icon-enabled="false"
      :second-button-disabled="false"
      size="large"
      class="include-exam-after-transferred"
      @first-cta="closeInclusionExamModal()"
      @second-cta="confirmIncludeExam()"
      @close="clearInclusionExamData()"
      style="
        max-width: 95% !important;
        max-height: 95% !important;
        min-height: 80vh !important;
      "
    >
      <traceability-exam-inclusion
        ref="includeExamComponent"
        :exam="body"
        class="mb-n8"
      />
    </modal>

    <modal
      ref="confirmDeleteSample"
      title="Confirmar exclusão"
      template="question"
      ref-name="confirmDeleteSample"
      first-button-text="Cancelar"
      second-button-text="Excluir"
      :close-button-enabled="true"
      :icon-enabled="false"
      :second-button-disabled="false"
      size="small"
      class="confirm-delete-sample-modal"
      @first-cta="closeDeleteExamModal()"
      @second-cta="confirmDeleteExamAction()"
    >
      <span>Tem certeza que deseja excluir este exame?</span>
    </modal>

    <ExamStatusModal
      ref="examStatusModal"
      :loading-title="modalTitles.loading"
      :success-title="modalTitles.success"
      :error-title="modalTitles.error"
      :success-info="modalTitles.info"
      @closed="onExamStatusModalClosed"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Moment from 'moment'
import strings from '@/strings'
import {
  TRACEABILITY_SEARCH_PATIENT_NAME,
  TRACEABILITY_SEARCH_OS_ALVARO,
  TRACEABILITY_SEARCH_OS_LIS,
  TRACEABILITY_SEARCH_BATCH,
} from '@/enums'
import { downloadFileByByteArray } from '@/utils/convertByteArrayToFile'
import startCase from 'lodash/startCase'
import { PDFDocument } from 'pdf-lib'

import resultPDFService from '../../api/resultsPDF'
import notify from '../../api/notification'
import examApi from '@/api/exam'

import DataTable from '../common/dataTable/DataTable'
import DataCell from '../common/dataTable/DataCell'
import Spinner from '../common/Spinner'
import TraceabilityProgressBar from './TraceabilityProgressBar.vue'
import DataShowOff from '../common/dataTable/DataShowOff'
import TraceabilityShowReadyExamsModal from './TraceabilityShowReadyExamsModal'
import TraceabilityCollectionDateModal from './TraceabilityCollectionDateModal.vue'
import TraceabilityExamInclusion from './TraceabilityExamInclusion.vue'
import DropdownActions from '../common/DropdownActions.vue'
import ExamStatusModal from '../common/ExamStatusModal.vue'
import {
  ALLOWED_STATUSES_FOR_DELETE_EXAM_ACTIONS,
  ALLOWED_STATUSES_FOR_INCLUD_EXAM_ACTIONS,
  ALLOWED_STATUSES_FOR_UPDATE_COLLECT_DATE_EXAM_ACTIONS,
} from '../../enums'

const EMPTY_FIELD = '-'
const EMPTY_FIELD_OS_ALVARO = 'OS não transferida'
const EMPTY_FIELD_OS_LIS = 'OS não informada'

export default {
  components: {
    DataTable,
    DataCell,
    Spinner,
    TraceabilityProgressBar,
    DataShowOff,
    TraceabilityShowReadyExamsModal,
    TraceabilityCollectionDateModal,
    TraceabilityExamInclusion,
    DropdownActions,
    ExamStatusModal,
  },
  filters: {
    capitalize: function (value) {
      return startCase(value.toLowerCase())
    },
    date: function (value) {
      return Moment(value).format('DD/MM/YY')
    },
    hour: function (value) {
      return Moment(value).format('HH:mm')
    },
  },
  props: {
    body: {
      type: Object,
      default: () => ({}),
    },
    orderIndex: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      PARAM_SEARCH_OS_ALVARO: TRACEABILITY_SEARCH_OS_ALVARO,
      PARAM_SEARCH_OS_LIS_ALVARO: TRACEABILITY_SEARCH_OS_LIS,
      PARAM_SEARCH_PATIENT_NAME: TRACEABILITY_SEARCH_PATIENT_NAME,
      EMPTY_FIELD: EMPTY_FIELD,
      EMPTY_FIELD_OS_LIS: EMPTY_FIELD_OS_LIS,
      EMPTY_FIELD_OS_ALVARO: EMPTY_FIELD_OS_ALVARO,
      status: strings.traceabilityStatus,
      table: {
        subHeader: [
          {
            title: 'AMOSTRA(S) / LOTE(S)',
            size: 15,
          },
          {
            title: 'EXAME',
            size: 10,
          },
          {
            title: 'STATUS',
            size: 35,
          },
          {
            title: 'DATA ATUALIZAÇÃO',
            size: 15,
          },
          {
            title: 'DATA PREVISTA',
            size: 15,
          },
          {
            title: '',
            size: 10,
          },
        ],
      },
      isDownloading: false,
      htmlForDownload: new Uint8Array(),
      isModalVisible: false,
      selectedSampleDate: null,
      selectedSampleTime: null,
      examToDelete: null,
      lastIncludedOrderId: null,
      lastDeletedOrderId: null,
      modalTitles: {
        loading: 'Processando Exame',
        success: 'Operação Realizada com Sucesso!',
        error: 'Erro na Operação',
        info: 'A atualização pode levar alguns segundos para aparecer na listagem devido ao processamento.',
      },
    }
  },
  computed: {
    ...mapGetters(['traceabilityOrders', 'traceabilityStatus', 'profile']),
    emptyFieldDueDate: function () {
      return this.traceabilityStatus === 'FINALIZADO' ? '' : EMPTY_FIELD
    },
    searchParam: function () {
      return this.$router.history.current.query.typeOfSearch
    },
    formattedDestinations: function () {
      if (!this.body.destinations || typeof this.body.destinations !== 'string') {
        return ''
      }

      const destinationsArray = this.body.destinations
        .split(',')
        .map(dest => dest.trim())
        .filter(dest => dest.length > 0)

      if (destinationsArray.length === 0) {
        return ''
      }

      if (destinationsArray.length === 1) {
        return destinationsArray[0]
      }

      return destinationsArray.join(', ')
    },
    samplesText: function () {
      const count = this.body.samples.length
      return `${count} amostra${count > 1 ? 's' : ''}`
    },
    pdfActionsItems: function () {
      return [
        {
          icon: 'icon-download-pdf',
          label: 'Exportar em PDF',
          action: () => this.downloadReadyExamsByOrder(this.body),
        },
        {
          icon: 'icon-eye-alt',
          label: 'Visualizar',
          action: () => this.createPdfToSeeResults(this.body),
        },
      ]
    },
    hasLaudoLiberado: function () {
      return this.body.samples.some(sample =>
        sample.exams.some(
          exam =>
            exam.currentStatus === 'LAUDO_LIBERADO' || exam.currentStatus === 'FINALIZADO'
        )
      )
    },
  },
  methods: {
    ...mapActions([
      'expandTraceabilityDetails',
      'setTraceabilityMapModalVisible',
      'selectTraceabilityParams',
      'getTraceabilityGroupedExams',
      'getTraceabilityGetGroupedExamsCount',
      'setCurrentTabTraceability',
      'resetTraceabilityPagination',
    ]),

    // ========================================
    // MÉTODOS DE NAVEGAÇÃO E INTERFACE
    // ========================================

    openMap({ orderId, sample }) {
      this.$emit('openMapModal', { orderId, sample })

      this.$analytics.pushAnalyticsEvent('ga-event', {
        category: 'Rastreabilidade',
        action: 'Abrir mapa de amostras em trânsito',
        label: sample.exams?.[0]?.id || 'unknown',
        'id-user': this.profile.id,
        'id-lab': this.profile.id_laboratory,
        'name-lab': this.profile.laboratoryName,
      })
    },
    showDueDate(exam) {
      const status = exam.currentStatus
      const hideDueDate = [
        'AMOSTRA_DESCARTADA',
        'AMOSTRA_OBTIDA',
        'CANCELADO',
        'NAO_ENVIADA',
        'NAO_RASTREADO',
        'RETIRADA_EXPIRADA',
        'EM_TRANSITO',
        'TRIAGEM',
        'EM_TRANSITO_ENTRE_NTO',
      ]
      if (hideDueDate.indexOf(status) > -1) return false
      if (exam.dueDate) return true
      return false
    },

    async goToLotDay(sample) {
      try {
        const searchParams = {
          typeOfSearch: TRACEABILITY_SEARCH_BATCH,
          q: sample.lotId,
          status: null,
          startDate: null,
          endDate: null,
        }

        await this.selectTraceabilityParams(searchParams)

        this.resetTraceabilityPagination()

        await this.getTraceabilityGroupedExams()
        await this.getTraceabilityGetGroupedExamsCount()

        this.setCurrentTabTraceability(0)

        this.$router.replace({
          path: '/traceability/search',
          query: {
            typeOfSearch: TRACEABILITY_SEARCH_BATCH,
            q: sample.lotId,
          },
        })
      } catch (error) {
        console.error('Erro ao filtrar por lote:', error)
        notify({
          title: 'Erro',
          message: 'Não foi possível realizar a busca pelo lote.',
        })
      }
    },

    // ========================================
    // MÉTODOS DE FORMATAÇÃO E CONVERSÃO
    // ========================================

    convertDataWithoutTimeZoneForDate(dateString) {
      if (!dateString) return '-'

      let dateTime = Moment.utc(dateString)

      const formattedDate = dateTime.format('DD/MM/YYYY')
      const formattedTime = dateTime.format('HH:mm')

      return formattedDate + ' às ' + formattedTime
    },

    // ========================================
    // MÉTODOS DE DOWNLOAD E PDF
    // ========================================

    downloadReadyExamsByOrder(order) {
      if (!this.profile.defaulter) {
        if (!this.isDownloading) {
          this.isDownloading = true

          resultPDFService
            .downloadResultsPDF(order.orderId, [])
            .then(response => {
              this.isDownloading = false
              const fileName = order.orderId
              const fileExtension = 'application/pdf'
              downloadFileByByteArray(response.data, fileName, fileExtension)
            })
            .catch(error => {
              this.isDownloading = false

              const errorMessage =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                this.$strings.resultsPDFErrorsMessages.downloadFail

              notify({
                title: 'AVISO:',
                message: errorMessage,
              })
            })
        }
      } else {
        this.$store.commit('RESULT_SET_OVERDUE', true)
      }
    },
    seeReadyExamsByOrder(order) {
      if (this.profile.defaulter) {
        notify({
          title: 'AVISO:',
          message:
            'Acesso bloqueado: Entre em contato com o time de atendimento através do 0800 643 8100 ou pelo e-mail: cobranca@dasa.com.br',
        })
        return Promise.reject('Usuário inadimplente.')
      }

      if (this.isDownloading) {
        return Promise.reject('Download já em andamento.')
      }

      this.isDownloading = true

      return resultPDFService
        .downloadResultsPDF(order.orderId, [])
        .then(response => {
          this.isDownloading = false
          return response.data
        })
        .catch(error => {
          this.isDownloading = false
          const errorMessage =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            this.$strings.resultsPDFErrorsMessages.downloadFail

          notify({
            title: 'AVISO:',
            message: errorMessage,
          })
          throw error
        })
    },

    async createPdfToSeeResults(order) {
      try {
        const pdfDoc = await PDFDocument.create()
        const pdfBuffer = await this.seeReadyExamsByOrder(order)
        const pdf = await PDFDocument.load(pdfBuffer)
        const copiedPages = await pdfDoc.copyPages(pdf, pdf.getPageIndices())

        copiedPages.forEach(page => pdfDoc.addPage(page))

        this.htmlForDownload = await pdfDoc.save()
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          'Ocorreu um erro ao gerar a visualização.'

        notify({
          title: 'AVISO:',
          message: errorMessage,
        })
      }
    },

    // ========================================
    // MÉTODOS DE ATUALIZAÇÃO DE DATA
    // ========================================

    openUpdateCollectionDateModal() {
      this.selectedSampleDate = this.body.collectionDate
      this.selectedSampleTime = this.body.collectionTime
      this.$refs.updateCollectionDateModal.openModal()
    },
    async handleUpdateCollectionDate({ date, time, resolve, reject }) {
      try {
        const dateTimeString = `${date} ${time}`
        const isoDate = Moment(dateTimeString, 'DD/MM/YYYY HH:mm').toISOString()

        const response = await examApi.updateCollectionDate({
          idOsAlvaro: this.body.orderId,
          dataColeta: isoDate,
        })

        const successMessage =
          response?.data?.message ||
          response?.message ||
          'Data de coleta atualizada com sucesso.'

        notify({
          title: 'Sucesso!',
          message: successMessage,
        })

        this.$emit('collectionDateUpdated', { orderId: this.body.orderId, date, time })

        if (resolve) resolve()
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          'Não foi possível atualizar a data de coleta.'

        notify({
          title: 'Erro ao atualizar',
          message: errorMessage,
        })

        if (reject) reject(error)
      }
    },

    // ========================================
    // MÉTODOS UTILITÁRIOS
    // ========================================

    buildApiPayload(examData, sample) {
      return {
        idOsAlvaro: this.body.orderId,
        code: examData.details?.code || examData.code,
        sample: {
          codeSample: sample.codeSample,
          material: examData.sample?.material,
          amount: examData.sample?.amount || 1,
          collectTimes: examData.sample?.collect_times || [],
        },
        additionalFieldValues: examData.additional_field_values || [],
        notes: examData.notes || '',
        examOrigin: examData.examOrigin || 'M',
        urgent: examData.urgent || false,
        docDelivered: examData.docDelivered || false,
        docRequired: examData.docRequired || false,
        allowDuplicate: examData.allowDuplicate || false,
      }
    },

    extractErrorMessage(error) {
      return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Erro desconhecido ao adicionar exame'
      )
    },

    extractSuccessMessage(response) {
      return response?.data?.message || response?.message || 'Exame incluído com sucesso.'
    },

    updateComponentStatus(status, sampleCode = null) {
      if (!this.$refs.includeExamComponent) return

      switch (status) {
        case 'start':
          this.$refs.includeExamComponent.startProcessing()
          break
        case 'stop':
          this.$refs.includeExamComponent.stopProcessing()
          break
        case 'processing':
        case 'success':
        case 'error':
          if (sampleCode) {
            this.$refs.includeExamComponent.setSampleStatus(sampleCode, status)
          }
          break
      }
    },

    formatErrorMessages(errorMessages) {
      if (errorMessages.length === 0) {
        return 'Não foi possível incluir o exame em nenhuma das amostras disponíveis.'
      }

      if (errorMessages.length === 1) {
        return errorMessages[0].message
      }

      const lastErrorMessage = errorMessages[errorMessages.length - 1].message
      return `Não foi possível incluir o exame em nenhuma das ${errorMessages.length} amostras disponíveis. Último erro: ${lastErrorMessage}`
    },

    // ========================================
    // PROCESSAMENTO DE INCLUSÃO DE EXAME
    // ========================================

    async processSampleInclusion(examData, sample) {
      this.updateComponentStatus('processing', sample.codeSample)

      try {
        const apiPayload = this.buildApiPayload(examData, sample)
        const response = await examApi.includeExam(apiPayload)

        this.updateComponentStatus('success', sample.codeSample)
        return {
          success: true,
          message: this.extractSuccessMessage(response),
        }
      } catch (error) {
        this.updateComponentStatus('error', sample.codeSample)
        await new Promise(resolve => setTimeout(resolve, 500))

        return {
          success: false,
          error: {
            sample: sample.codeSample,
            message: this.extractErrorMessage(error),
          },
        }
      }
    },

    // ========================================
    // HANDLERS DE RESULTADO
    // ========================================

    handleInclusionFailure(errorMessages) {
      const errorMessage = this.formatErrorMessages(errorMessages)
      this.$refs.examStatusModal.showError(errorMessage)
    },

    // ========================================
    // MÉTODO PRINCIPAL DE INCLUSÃO
    // ========================================

    async addExamToSamples(examData) {
      const samples = examData.availableSamples
      const errorMessages = []
      let successMessage = null

      this.updateComponentStatus('start')

      for (const sample of samples) {
        const result = await this.processSampleInclusion(examData, sample)

        if (result.success) {
          successMessage = result.message
          break
        } else {
          errorMessages.push(result.error)
        }
      }

      this.updateComponentStatus('stop')

      if (successMessage) {
        return true
      } else {
        this.handleInclusionFailure(errorMessages)
        return false
      }
    },

    // ========================================
    // MÉTODOS DE CONFIGURAÇÃO DE MODAL
    // ========================================

    setModalTitlesForInclusion() {
      this.modalTitles = {
        loading: 'Incluindo Exame',
        success: 'Exame Adicionado com Sucesso!',
        error: 'Erro ao Incluir Exame',
        info: 'O exame pode levar alguns segundos para aparecer na listagem devido ao processamento.',
      }
    },

    setModalTitlesForDeletion() {
      this.modalTitles = {
        loading: 'Excluindo Exame',
        success: 'Exame Excluído com Sucesso!',
        error: 'Erro ao Excluir Exame',
        info: 'A atualização pode levar alguns segundos para aparecer na listagem devido ao processamento.',
      }
    },

    // ========================================
    // MÉTODOS DE EXCLUSÃO DE EXAME
    // ========================================

    async deleteExam(exam) {
      try {
        this.setModalTitlesForDeletion()
        this.$refs.examStatusModal.showLoading('Processando exclusão do exame...')

        await examApi.deleteExam({
          idOsAlvaro: this.body.orderId,
          code: exam.codeExamBrand,
          codeSample: exam.codeSample,
        })

        this.lastDeletedOrderId = this.body.orderId
        this.$refs.examStatusModal.showSuccess()
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          'Não foi possível excluir o exame.'

        this.$refs.examStatusModal.showError(errorMessage)
      }
    },

    openDeleteExamModal(exam) {
      this.examToDelete = exam
      this.$refs.confirmDeleteSample.open()
    },

    closeDeleteExamModal() {
      this.examToDelete = null
      this.$refs.confirmDeleteSample.close()
    },

    confirmDeleteExamAction() {
      if (this.examToDelete) {
        this.deleteExam(this.examToDelete)
        this.closeDeleteExamModal()
      }
    },

    // ========================================
    // MÉTODOS DE MODAL DE INCLUSÃO
    // ========================================

    openInclusionExamModal(bodyData) {
      this.$refs.traceabilitytraceabilityInsertExamAfterTransferred.open()
      this.$nextTick(() => {
        if (this.$refs.includeExamComponent) {
          this.$refs.includeExamComponent.selectedSampleCode = null
          this.$refs.includeExamComponent.resetSampleStatuses()
          this.$refs.includeExamComponent.checkAndSelectSingleSample()
        }
      })
    },
    closeInclusionExamModal() {
      this.clearInclusionExamData()
      this.$refs.traceabilitytraceabilityInsertExamAfterTransferred.close()
    },

    clearInclusionExamData() {
      if (this.$refs.includeExamComponent) {
        this.$refs.includeExamComponent.selectedSampleCode = null
        this.$refs.includeExamComponent.$store.dispatch('clearExamList')
        this.$refs.includeExamComponent.resetSampleStatuses()
      }
    },
    async confirmIncludeExam() {
      try {
        const examData = this.$refs.includeExamComponent.getExamData()

        if (examData) {
          if (!examData.availableSamples || examData.availableSamples.length === 0) {
            notify({
              title: 'Aviso',
              message: 'Não há amostras disponíveis para adicionar o exame.',
            })
            return
          }

          if (!examData.canStartProcess) {
            notify({
              title: 'Aviso',
              message:
                'Aguarde o processo atual finalizar ou verifique se um exame foi selecionado.',
            })
            return
          }

          this.setModalTitlesForInclusion()
          this.$refs.examStatusModal.showLoading('Processando inclusão do exame...')
          this.closeInclusionExamModal()

          const success = await this.addExamToSamples(examData)

          if (success) {
            this.lastIncludedOrderId = this.body.orderId
            this.$refs.examStatusModal.showSuccess()
          }
        } else {
          notify({
            title: 'Aviso',
            message: 'Por favor, preencha os dados do exame.',
          })
        }
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          'Ocorreu um erro inesperado ao incluir o exame.'

        this.$refs.examStatusModal.showError(errorMessage)
      }
    },

    // ========================================
    // MÉTODOS DE VALIDAÇÃO E PERMISSÕES
    // ========================================

    canIncludeExam(order) {
      return order.samples.some(sample =>
        sample.exams.some(exam =>
          ALLOWED_STATUSES_FOR_INCLUD_EXAM_ACTIONS.includes(exam.currentStatus)
        )
      )
    },

    canDeleteExam(exam) {
      if (!ALLOWED_STATUSES_FOR_DELETE_EXAM_ACTIONS.includes(exam.currentStatus)) {
        return false
      }

      const totalExams = this.body.samples.reduce((total, sample) => {
        const activeExams = sample.exams.filter(
          examItem =>
            examItem.currentStatus !== 'CANCELADO' &&
            examItem.currentStatus !== 'CANCELADO_CLIENTE'
        )
        return total + activeExams.length
      }, 0)

      return totalExams > 1
    },

    canUpdateCollectionDate(order) {
      return order.samples.some(sample =>
        sample.exams.some(exam =>
          ALLOWED_STATUSES_FOR_UPDATE_COLLECT_DATE_EXAM_ACTIONS.includes(
            exam.currentStatus
          )
        )
      )
    },

    onExamStatusModalClosed(status) {
      if (status === 'success') {
        if (this.lastIncludedOrderId) {
          this.$emit('examIncluded', {
            orderId: this.lastIncludedOrderId,
            examData: null,
          })

          const searchParams = {
            typeOfSearch: this.PARAM_SEARCH_OS_ALVARO,
            q: this.lastIncludedOrderId,
            status: null,
            startDate: null,
            endDate: null,
          }

          this.selectTraceabilityParams(searchParams)
          this.resetTraceabilityPagination()
          this.getTraceabilityGroupedExams()
          this.getTraceabilityGetGroupedExamsCount()
          this.setCurrentTabTraceability(0)

          this.$router.replace({
            path: '/traceability/search',
            query: {
              typeOfSearch: this.PARAM_SEARCH_OS_ALVARO,
              q: this.lastIncludedOrderId,
            },
          })

          this.lastIncludedOrderId = null
        }

        if (this.lastDeletedOrderId) {
          const searchParams = {
            typeOfSearch: this.PARAM_SEARCH_OS_ALVARO,
            q: this.lastDeletedOrderId,
            status: null,
            startDate: null,
            endDate: null,
          }

          this.selectTraceabilityParams(searchParams)
          this.resetTraceabilityPagination()
          this.getTraceabilityGroupedExams()
          this.getTraceabilityGetGroupedExamsCount()
          this.setCurrentTabTraceability(0)

          this.$router.replace({
            path: '/traceability/search',
            query: {
              typeOfSearch: this.PARAM_SEARCH_OS_ALVARO,
              q: this.lastDeletedOrderId,
            },
          })

          this.lastDeletedOrderId = null
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.is-finished {
  cursor: pointer;

  .loader {
    margin-top: 2px;
    width: 24px;
    height: 24px;
    border-top: 0.5em solid rgba(255, 255, 255, 0.2);
    border-right: 0.5em solid rgba(255, 255, 255, 0.2);
    border-bottom: 0.5em solid rgba(255, 255, 255, 0.2);
    border-left: 0.5em solid #ffffff;
  }
}

.subtable {
  background: #f3f3f3;
  border-radius: 3px;
  padding: 0px 0px 12px 0px !important;
  cursor: default;

  .data-table__header__row__cell {
    padding-top: 0px !important;

    &:nth-child(4n) {
      text-align: right;
      padding-right: 24px;
    }

    &:last-child {
      text-align: right;
      padding-right: 24px;
    }
  }

  .data-table__header__row {
    display: flex;
  }

  .lot-box {
    display: flex;
    align-items: self-start;
  }

  .exam {
    font-family: 'Exo 2', sans-serif !important;
    font-style: normal;
    font-weight: normal;
    font-size: 15px;
    line-height: 26px;
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .details {
    font-family: 'Exo 2', sans-serif !important;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 26px;
    white-space: nowrap;
    width: 100%;
    text-overflow: ellipsis;
    overflow: hidden;
    display: flex;
  }

  .lot {
    background: #999999;
    padding: 2px 8px;
    text-transform: uppercase;
    color: #fff;
    font-weight: 600;
    border-radius: 4px;
    letter-spacing: 0.5px;
    font-size: 13px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-family: 'Exo 2', sans-serif !important;

    &.is-link {
      transition: all ease 0.3s;
      cursor: pointer;

      &:hover {
        background-color: rgba(212, 43, 140, 0.48);
      }
    }
  }
}

.delayed {
  &-icon {
    width: 18px;
    min-width: 13px;
    margin-bottom: 4px;

    & > svg {
      path {
        fill: #f5a623;
      }
    }
  }

  &-color {
    color: #f5a623;
  }

  &-warning {
    margin-left: 5px;
  }
}

.overflow-text {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.highlight-search {
  color: #47ba8d;
}

.status {
  font-weight: 500;
  display: flex;
  flex-direction: row;
  margin-top: 2px;

  p {
    display: flex;
    font-size: 14px;
    width: 75%;
    margin-top: 2px;
  }

  .button-open-map {
    cursor: pointer;

    &:hover,
    &:focus {
      .md-icon > svg {
        .pin {
          fill: #7f1a54;
          transition: 0.5s;
        }
      }
    }
  }

  .md-icon {
    margin: 0 auto;
  }

  .bullet {
    margin-right: 0.5em;
    font-size: 25px;
    font-weight: bold;
  }
}

.btn-inline {
  min-width: 40px;

  i {
    width: 20px;
    height: 20px;
    min-width: 20px;
    padding-top: 4px;

    path {
      fill: white;
    }
  }
}

.new-subtable {
  &__exam-details {
    height: 65px;

    p {
      font-size: 14px;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }

    .batch {
      width: 100%;

      span {
        background-color: #999;
        color: #fff;
        font-weight: 600;
        padding: 4px 8px;
        border-radius: 4px;
        margin-top: 8px;
        display: inline-block;
      }
    }
  }

  &__promised-date {
    font-size: 14px;
    display: flex;
    align-items: center;
  }
}

.traceability_sample_exam {
  margin-top: 12px;
  display: flex;
  flex-direction: row;

  &__brand {
    margin-top: 8px;
    height: 32px;
    padding: 0px 0px 10px 0px;

    p {
      font-size: 14px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &__progress_bar {
    padding: 0px 10px 10px 0px;
  }

  &__date {
    text-align: right;
    align-items: normal;
  }

  &__date-atualização {
    text-align: right;
    align-items: center;
    justify-content: flex-end;
    padding: 6px 18px 10px 10px;
    display: flex;
    flex-direction: column;
    width: 100%;

    p {
      width: 100%;
      text-align: right;
      justify-content: flex-end;
      display: flex;
    }
  }
}

.separator {
  border-top: 2px solid #fff;

  &:last-of-type {
    border-bottom: none;
  }

  &:first-of-type {
    border-bottom: none;
  }

  .exam-row {
    border-bottom: 1px solid #f1f3f4;

    &:hover {
      background-color: #f8f9fa;
    }
  }
}

.w-100 {
  width: 100%;
}

.expand-enter-active,
.expand-leave-active {
  transition:
    max-height 0.3s ease,
    opacity 0.3s ease;
}

.expand-enter,
.expand-leave-to {
  opacity: 0;
  overflow: hidden;
}

.column-center {
  display: flex;
  align-items: center;
  margin: 0;
  flex-direction: row;
  justify-content: center;
  gap: 5px;
}

.status-icon {
  max-width: 30px;
  max-height: 30px;
}
</style>

<style lang="scss">
#traceabilitytraceabilityInsertExamAfterTransferred {
  .md-dialog {
    min-height: max-content !important;
  }
}
</style>
